import React, { useState, useEffect } from "react";
import { Plus, Edit, Trash2, Users, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import api from "../utils/api";
import { formatMXN } from "../utils/format";

function PriceListForm({ priceList, products, onClose, onSave }) {
  const [form, setForm] = useState({
    name: "", description: "", is_general: true, items: []
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (priceList) setForm({ ...priceList });
  }, [priceList]);

  const addItem = () => {
    setForm((f) => ({
      ...f, items: [...f.items, { product_id: "", product_name: "", tiers: [{ min_qty: 0, max_qty: null, price: 0 }] }]
    }));
  };

  const updateItemProduct = (i, productId) => {
    const prod = products.find((p) => p.id === productId);
    const items = [...form.items];
    items[i] = { ...items[i], product_id: productId, product_name: prod?.name || "" };
    setForm((f) => ({ ...f, items }));
  };

  const addTier = (itemIdx) => {
    const items = [...form.items];
    items[itemIdx].tiers = [...(items[itemIdx].tiers || []), { min_qty: 0, max_qty: null, price: 0 }];
    setForm((f) => ({ ...f, items }));
  };

  const updateTier = (itemIdx, tierIdx, field, value) => {
    const items = [...form.items];
    items[itemIdx].tiers[tierIdx] = { ...items[itemIdx].tiers[tierIdx], [field]: value === "" ? null : Number(value) };
    setForm((f) => ({ ...f, items }));
  };

  const handleSave = async () => {
    if (!form.name) { toast.error("El nombre es requerido"); return; }
    setSaving(true);
    try {
      if (priceList) {
        await api.put(`/price-lists/${priceList.id}`, form);
        toast.success("Lista actualizada");
      } else {
        await api.post("/price-lists", form);
        toast.success("Lista creada");
      }
      onSave();
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  const finalProducts = products.filter((p) => p.category === "producto_final");

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="price-list-form">
        <DialogHeader>
          <DialogTitle>{priceList ? "Editar lista de precios" : "Nueva lista de precios"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Nombre *</label>
              <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
                placeholder="Ej: Precio Mayoreo, Precio Especial Cliente X" />
            </div>
            <div className="flex items-center gap-3 mt-5">
              <input type="checkbox" id="is_general" checked={form.is_general}
                onChange={(e) => setForm((f) => ({ ...f, is_general: e.target.checked }))}
                className="w-4 h-4 accent-brand-600" />
              <label htmlFor="is_general" className="text-sm text-slate-700">Lista general (aplica a todos)</label>
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-slate-500 mb-1">Descripción</label>
              <input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
            </div>
          </div>

          {/* Products with tiers */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-700">Productos y precios por volumen</h3>
              <button onClick={addItem} className="text-xs text-brand-600 hover:text-brand-700 font-medium">+ Agregar producto</button>
            </div>
            <div className="space-y-4">
              {form.items.map((item, i) => (
                <div key={i} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <Select value={item.product_id} onValueChange={(v) => updateItemProduct(i, v)}>
                      <SelectTrigger className="w-64"><SelectValue placeholder="Seleccionar producto..." /></SelectTrigger>
                      <SelectContent>
                        {finalProducts.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <button onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }))}
                      className="text-red-400 hover:text-red-600 text-xs">✕ Quitar</button>
                  </div>
                  <div className="space-y-2">
                    <div className="grid grid-cols-4 gap-2 text-xs font-semibold text-slate-500 px-1">
                      <span>Desde (kg)</span><span>Hasta (kg)</span><span>Precio/unidad</span><span></span>
                    </div>
                    {(item.tiers || []).map((tier, ti) => (
                      <div key={ti} className="grid grid-cols-4 gap-2 items-center">
                        <input type="number" value={tier.min_qty || 0} onChange={(e) => updateTier(i, ti, "min_qty", e.target.value)}
                          className="border border-slate-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500" />
                        <input type="number" placeholder="Sin límite" value={tier.max_qty ?? ""}
                          onChange={(e) => updateTier(i, ti, "max_qty", e.target.value)}
                          className="border border-slate-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500" />
                        <input type="number" value={tier.price || 0} onChange={(e) => updateTier(i, ti, "price", e.target.value)}
                          className="border border-slate-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500" step="0.01" />
                        <button onClick={() => {
                          const items = [...form.items];
                          items[i].tiers = items[i].tiers.filter((_, idx) => idx !== ti);
                          setForm((f) => ({ ...f, items }));
                        }} className="text-red-400 hover:text-red-600 text-xs">✕</button>
                      </div>
                    ))}
                    <button onClick={() => addTier(i)} className="text-xs text-brand-600 hover:text-brand-700 mt-1">+ Agregar rango</button>
                  </div>
                </div>
              ))}
              {form.items.length === 0 && (
                <p className="text-center text-slate-400 text-sm py-4">Agrega productos con sus rangos de precio por volumen</p>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">Cancelar</button>
          <button onClick={handleSave} disabled={saving}
            className="px-6 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-60">
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ListasPrecios() {
  const [lists, setLists] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [assigningList, setAssigningList] = useState(null); // price list for customer assignment
  const [listCustomers, setListCustomers] = useState([]); // customers assigned to current list
  const [allCustomers, setAllCustomers] = useState([]);
  const [assignSearch, setAssignSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [lr, pr, cr] = await Promise.all([api.get("/price-lists"), api.get("/products"), api.get("/customers")]);
      setLists(lr.data); setProducts(pr.data); setAllCustomers(cr.data);
    } catch { toast.error("Error al cargar"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta lista?")) return;
    await api.delete(`/price-lists/${id}`);
    toast.success("Lista eliminada");
    load();
  };

  const openAssign = async (list) => {
    setAssigningList(list);
    setAssignSearch("");
    const r = await api.get(`/price-lists/${list.id}/customers`);
    setListCustomers(r.data.map((c) => c.id));
  };

  const toggleAssignCustomer = async (customerId) => {
    if (!assigningList) return;
    const isAssigned = listCustomers.includes(customerId);
    try {
      if (isAssigned) {
        await api.delete(`/price-lists/${assigningList.id}/assign-customer/${customerId}`);
        setListCustomers((prev) => prev.filter((id) => id !== customerId));
        toast.success("Cliente removido de la lista");
      } else {
        await api.post(`/price-lists/${assigningList.id}/assign-customer/${customerId}`);
        setListCustomers((prev) => [...prev, customerId]);
        toast.success("Cliente asignado a la lista");
      }
    } catch { toast.error("Error al actualizar asignación"); }
  };

  const filteredCustomers = allCustomers.filter((c) =>
    c.name?.toLowerCase().includes(assignSearch.toLowerCase()) ||
    c.rfc?.toLowerCase().includes(assignSearch.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="listas-precios-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Listas de Precios</h1>
          <p className="text-slate-500 text-sm mt-1">Precios por volumen y por cliente</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="nueva-lista-btn"
          className="flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          <Plus size={16} /> Nueva lista
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-3 text-center py-12 text-slate-400">Cargando...</div>
        ) : lists.length === 0 ? (
          <div className="col-span-3 text-center py-12 text-slate-400">Sin listas de precios</div>
        ) : lists.map((list) => (
          <div key={list.id} data-testid={`price-list-${list.id}`}
            className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-semibold text-slate-900">{list.name}</div>
                {list.description && <div className="text-xs text-slate-500 mt-0.5">{list.description}</div>}
              </div>
              <div className="flex items-center gap-1">
                {list.is_general && (
                  <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full font-medium">General</span>
                )}
                <button onClick={() => openAssign(list)} title="Asignar clientes"
                  className="p-1.5 hover:bg-violet-50 rounded-md transition-colors">
                  <Users size={13} className="text-violet-500" />
                </button>
                <button onClick={() => { setEditing(list); setShowForm(true); }} className="p-1.5 hover:bg-slate-100 rounded-md transition-colors">
                  <Edit size={13} className="text-slate-500" />
                </button>
                <button onClick={() => handleDelete(list.id)} className="p-1.5 hover:bg-red-50 rounded-md transition-colors">
                  <Trash2 size={13} className="text-red-400" />
                </button>
              </div>
            </div>
            <div className="text-xs text-slate-500">{list.items?.length || 0} productos configurados</div>
            {(list.items || []).slice(0, 3).map((item) => (
              <div key={item.product_id} className="mt-2 text-xs">
                <span className="font-medium text-slate-700">{item.product_name}: </span>
                {(item.tiers || []).map((t, i) => (
                  <span key={i} className="text-slate-500">
                    {t.min_qty}–{t.max_qty || "∞"} kg = {formatMXN(t.price)}
                    {i < item.tiers.length - 1 ? ", " : ""}
                  </span>
                ))}
              </div>
            ))}
            {/* Assigned customers count */}
            <div className="mt-3 pt-2 border-t border-slate-100">
              <button onClick={() => openAssign(list)} className="text-xs text-violet-600 hover:underline flex items-center gap-1">
                <Users size={11} /> Asignar / ver clientes
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Assign customers modal */}
      {assigningList && (
        <Dialog open onOpenChange={() => setAssigningList(null)}>
          <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Clientes — {assigningList.name}</DialogTitle>
            </DialogHeader>
            <div className="py-2 space-y-3">
              <p className="text-xs text-slate-500">Los clientes con esta lista ven los precios configurados. Activa/desactiva con el toggle.</p>
              <div className="relative">
                <Search size={13} className="absolute left-2.5 top-2.5 text-slate-400" />
                <input value={assignSearch} onChange={(e) => setAssignSearch(e.target.value)}
                  placeholder="Buscar cliente..."
                  className="w-full border border-slate-200 rounded-md pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
              </div>
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {filteredCustomers.map((c) => {
                  const assigned = listCustomers.includes(c.id);
                  return (
                    <div key={c.id} onClick={() => toggleAssignCustomer(c.id)}
                      className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors ${assigned ? "bg-violet-50 border border-violet-200" : "hover:bg-slate-50 border border-transparent"}`}>
                      <div>
                        <div className="text-sm font-medium text-slate-800">{c.name}</div>
                        {c.rfc && <div className="text-xs text-slate-400">{c.rfc}</div>}
                      </div>
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${assigned ? "bg-violet-600 border-violet-600" : "border-slate-300"}`}>
                        {assigned && <span className="text-white text-xs font-bold leading-none">✓</span>}
                      </div>
                    </div>
                  );
                })}
                {filteredCustomers.length === 0 && <p className="text-xs text-slate-400 text-center py-4">Sin clientes</p>}
              </div>
              <div className="text-xs text-slate-500 font-medium">{listCustomers.length} cliente(s) asignado(s)</div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {showForm && (
        <PriceListForm priceList={editing} products={products} onClose={() => setShowForm(false)} onSave={() => { setShowForm(false); load(); }} />
      )}
    </div>
  );
}
